import React from "react";
import { MainMenu, useExcalidrawAPI } from "@excalidraw/excalidraw";

import { createShareLink, patchScene } from "../lib/plbxApi";
import { saveCurrentBoard } from "../lib/saveBoard";

const linkIcon = (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
    <path d="M11 3a1 1 0 100 2h2.586l-7.293 7.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
  </svg>
);

const cloudIcon = (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
    <path d="M5.5 16a4.5 4.5 0 01-.5-8.97 5 5 0 019.9-1A4 4 0 0114 16H5.5z" />
  </svg>
);

const keyIcon = (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
    <path d="M12.5 2a5.5 5.5 0 015.36 6.78l-1.07 4.46a1 1 0 01-.97.76H14v2a1 1 0 01-1 1h-2v2a1 1 0 01-1 1H7a1 1 0 01-1-1v-3.59A5.5 5.5 0 0112.5 2zm0 4a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
  </svg>
);

const pencilIcon = (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
    <path d="M14.69 2.66a2 2 0 012.83 2.83l-9.9 9.9-3.54.71.71-3.54 9.9-9.9z" />
  </svg>
);

/**
 * Plbx-specific MainMenu items.
 *
 * The Cmd/Ctrl+S save shortcut lives in PlbxSaveHotkey (always mounted), NOT
 * here — Radix unmounts MainMenu children while the menu is closed, so a
 * keydown effect on this component would be dormant during normal drawing. The
 * "Save to Playbox" item and the hotkey share saveCurrentBoard().
 */
export const MainMenuPlbxItems: React.FC = () => {
  const excalidrawAPI = useExcalidrawAPI();

  // Anonymous viewer on share.draw.plbx.ai — skip team menu items.
  const isShareViewer =
    typeof window !== "undefined" &&
    window.location.hostname.startsWith("share.");

  const onRenameBoard = async () => {
    if (!excalidrawAPI) return;
    const boardId: string | undefined = (window as any).__plbxBoardId;
    if (!boardId) {
      excalidrawAPI.setToast({
        message: "Save the board first (Save to Playbox)",
      });
      return;
    }
    const current = (window as any).__plbxBoardName || "";
    const next = window.prompt("Rename board:", current);
    if (!next || !next.trim() || next.trim() === current) return;
    try {
      const updated = await patchScene(boardId, { name: next.trim() });
      (window as any).__plbxBoardName = updated.name;
      excalidrawAPI.setToast({ message: `Renamed: ${updated.name}` });
    } catch (e: any) {
      excalidrawAPI.setToast({
        message: `Rename failed: ${e?.message || "unknown error"}`,
      });
    }
  };

  const onCopyShareLink = async () => {
    if (!excalidrawAPI) return;
    const boardId: string | undefined = (window as any).__plbxBoardId;
    if (!boardId) {
      excalidrawAPI.setToast({
        message: "Save the board first (Save to Playbox)",
      });
      return;
    }
    try {
      const { readOnlyUrl } = await createShareLink(boardId);
      await navigator.clipboard.writeText(readOnlyUrl);
      excalidrawAPI.setToast({ message: "Public link copied" });
    } catch (e: any) {
      excalidrawAPI.setToast({
        message: `Share failed: ${e?.message || "unknown error"}`,
      });
    }
  };

  if (isShareViewer) return null;

  return (
    <>
      <MainMenu.Separator />
      <MainMenu.ItemLink icon={linkIcon} href="/boards">
        My Boards
      </MainMenu.ItemLink>
      <MainMenu.Item
        icon={cloudIcon}
        onSelect={() => void saveCurrentBoard(excalidrawAPI)}
      >
        Save to Playbox (⌘S)
      </MainMenu.Item>
      <MainMenu.Item icon={pencilIcon} onSelect={onRenameBoard}>
        Rename board
      </MainMenu.Item>
      <MainMenu.Item icon={linkIcon} onSelect={onCopyShareLink}>
        Copy public link
      </MainMenu.Item>
      <MainMenu.ItemLink icon={keyIcon} href="/account/api-keys">
        API keys
      </MainMenu.ItemLink>
    </>
  );
};

export default MainMenuPlbxItems;
